import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  Stack,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Home as HomeIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../utils/config';
import { NoteIcon } from '../../../components/accounting/NoteIcon';

// Aging bucket colors: green (current) -> dark red (90+)
const AGING_COLORS: Record<string, string> = {
  current: '#2e7d32',
  '1-30': '#f9a825',
  '31-60': '#ef6c00',
  '61-90': '#d84315',
  '90+': '#b71c1c',
};

interface AgingBucket {
  key: string;
  label: string;
  pastDue: boolean;
  amount: number;
  count: number;
}

interface InvoiceAging {
  totalOutstanding: number;
  openCount: number;
  pastDueAmount: number;
  pastDueCount: number;
  pastDuePct: number;
  notYetDueAmount: number;
  notYetDueCount: number;
  totalInvoiced: number;
  totalCollected: number;
  collectedPct: number;
  buckets: AgingBucket[];
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  subsidiaryName?: string;
  totalAmount: number;
  balanceDue: number;
  customerMemo?: string;
  privateNote?: string;
  createdTime: string;
  status: 'open' | 'paid' | 'void';
  syncError?: string;
  currencyCode?: string;
  hasNotes?: boolean;
  correspondingBillId?: string | null;
}

interface InvoiceSummary {
  subsidiaryName: string;
  vendorId: string;
  invoiceCount: number;
  totalInvoiced: number;
  totalCollected: number;
  totalOutstanding: number;
}

export const InvoiceManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState<InvoiceSummary[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [aging, setAging] = useState<InvoiceAging | null>(null);
  const [agingLoading, setAgingLoading] = useState(true);
  const isFetchingRef = useRef(false);

  // Read filter state from URL search params (with defaults)
  const page = parseInt(searchParams.get('page') || '0', 10);
  const rowsPerPage = parseInt(searchParams.get('rowsPerPage') || '25', 10);
  const sortBy = searchParams.get('sortBy') || 'doc_number';
  const sortOrder = (searchParams.get('sortOrder') || 'desc') as 'asc' | 'desc';
  const selectedVendorId = searchParams.get('vendorId') || null;
  const showOnlyOpen = searchParams.get('showOnlyOpen') !== 'false'; // default true
  const period = (searchParams.get('period') === 'quarter' ? 'quarter' : 'ytd') as 'ytd' | 'quarter';

  // Helper to update URL params
  const updateSearchParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);
  
  const fetchInvoices = useCallback(async () => {
    // Prevent concurrent requests
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        offset: (page * rowsPerPage).toString(),
        limit: rowsPerPage.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
        period: period,
      });

      // Add optional filters
      if (selectedVendorId) {
        params.append('vendorId', selectedVendorId);
      }
      if (showOnlyOpen) {
        params.append('status', 'open');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/invoices?${params.toString()}`
      );

      if (!response || !response.ok) {
        throw new Error('Failed to fetch invoices');
      }

      const data = await response.json();
      setInvoices(data.data?.invoices || []);
      setTotalCount(data.data?.pagination?.total || 0);
      setError(null);
    } catch (err: any) {
      // Don't set error if it's an abort error
      if (err?.name !== 'AbortError') {
        console.error('Error fetching invoices:', err);
        setError('Failed to fetch invoices');
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [page, rowsPerPage, sortBy, sortOrder, selectedVendorId, showOnlyOpen, period, authenticatedFetch]);
  
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/invoices/summary?period=${period}`
      );

      if (!response || !response.ok) {
        throw new Error('Failed to fetch invoice summary');
      }

      const data = await response.json();
      setSummary(data.data?.summary || []);
    } catch (err: any) {
      console.error('Error fetching invoice summary:', err);
      // Don't show error, just log it
    } finally {
      setSummaryLoading(false);
    }
  }, [authenticatedFetch, period]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Aging KPIs + bar chart, scoped to the selected subsidiary (or all if none).
  const fetchAging = useCallback(async () => {
    setAgingLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (selectedVendorId) {
        params.set('vendorId', selectedVendorId);
      }
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/invoices/aging?${params.toString()}`
      );

      if (!response || !response.ok) {
        throw new Error('Failed to fetch invoice aging');
      }

      const data = await response.json();
      setAging(data.data || null);
    } catch (err: any) {
      console.error('Error fetching invoice aging:', err);
      setAging(null);
    } finally {
      setAgingLoading(false);
    }
  }, [authenticatedFetch, selectedVendorId, period]);

  useEffect(() => {
    fetchAging();
  }, [fetchAging]);

  const handlePeriodChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPeriod: 'ytd' | 'quarter' | null
  ) => {
    if (newPeriod) {
      updateSearchParams({ period: newPeriod, page: '0' });
    }
  };
  
  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchParams({ page: newPage.toString() });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateSearchParams({
      rowsPerPage: event.target.value,
      page: '0',
    });
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      updateSearchParams({
        sortOrder: sortOrder === 'asc' ? 'desc' : 'asc',
        page: '0',
      });
    } else {
      // Set new column and default to descending
      updateSearchParams({
        sortBy: column,
        sortOrder: 'desc',
        page: '0',
      });
    }
  };

  const handleSubsidiarySelect = (vendorId: string) => {
    // Toggle selection: if same card clicked, deselect; otherwise select
    const newVendorId = selectedVendorId === vendorId ? null : vendorId;
    updateSearchParams({
      vendorId: newVendorId,
      page: '0',
    });
  };

  const handleShowOnlyOpenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateSearchParams({
      showOnlyOpen: event.target.checked ? 'true' : 'false',
      page: '0',
    });
  };
  
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };
  
  const formatCurrency = (amount?: number, currencyCode?: string) => {
    if (amount === undefined || amount === null) return '-';
    const code = currencyCode || 'USD';
    const formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return code === 'USD' ? `$${formatted}` : `${code} ${formatted}`;
  };
  
  const truncateText = (text?: string, maxLength: number = 50) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getStatusChipProps = (status: string) => {
    switch (status) {
      case 'paid':
        return { color: 'success' as const, label: 'Paid' };
      case 'void':
        return { color: 'default' as const, label: 'Void' };
      case 'open':
      default:
        return { color: 'warning' as const, label: 'Open' };
    }
  };

  const renderSortIcon = (column: string) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? (
      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
    ) : (
      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
    );
  };

  return (
    <Box sx={{ py: 4, px: 3, width: '100%' }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        <Typography color="text.primary">Invoice Management</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ReceiptIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Invoice Management
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            color="primary"
            aria-label="Time period"
          >
            <ToggleButton value="ytd" aria-label="Year to date">YTD</ToggleButton>
            <ToggleButton value="quarter" aria-label="Current quarter">Quarter</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View and manage all QuickBooks invoices across vendors.
        </Typography>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Layout: Sidebar + Content */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Left Sidebar - Subsidiary Cards */}
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <Paper sx={{ p: 2, position: 'sticky', top: 16 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem' }}>Subsidiaries</Typography>
              <IconButton
                size="small"
                onClick={() => { fetchInvoices(); fetchSummary(); fetchAging(); }}
                title="Refresh"
                disabled={loading || summaryLoading}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
            {summaryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {summary.map((item) => {
                  const isSelected = selectedVendorId === item.vendorId;
                  return (
                    <Card
                      key={item.subsidiaryName}
                      variant="outlined"
                      onClick={() => handleSubsidiarySelect(item.vendorId)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderWidth: isSelected ? 2 : 1,
                        backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                        boxShadow: isSelected ? 2 : 0,
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                          backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {item.subsidiaryName}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">
                            Count:
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontWeight="medium">
                            {item.invoiceCount}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">
                            Invoiced:
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontWeight="medium">
                            {formatCurrency(item.totalInvoiced, 'USD')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">
                            Collected:
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontWeight="medium">
                            {formatCurrency(item.totalCollected, 'USD')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="error" fontWeight="medium">
                            Outstanding:
                          </Typography>
                          <Typography variant="caption" color="error" fontWeight="bold">
                            {formatCurrency(item.totalOutstanding, 'USD')}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Box>

        {/* Main Content - KPIs, Aging Chart, Filters and Table */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {/* Outstanding KPIs + Aging chart */}
          {agingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, mb: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : aging ? (
            <Box sx={{ mb: 2 }}>
              {/* KPI strip */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Card variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">Total Outstanding</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(aging.totalOutstanding, 'USD')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {aging.openCount} open {aging.openCount === 1 ? 'invoice' : 'invoices'}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ borderColor: aging.pastDueAmount > 0 ? 'error.light' : 'divider' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">Past Due</Typography>
                    <Typography variant="h5" fontWeight="bold" color="error">
                      {formatCurrency(aging.pastDueAmount, 'USD')}
                      <Typography component="span" variant="body2" color="error" sx={{ ml: 0.5 }}>
                        ({Math.round(aging.pastDuePct * 100)}%)
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {aging.pastDueCount} {aging.pastDueCount === 1 ? 'invoice' : 'invoices'}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">Not Yet Due</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(aging.notYetDueAmount, 'USD')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {aging.notYetDueCount} {aging.notYetDueCount === 1 ? 'invoice' : 'invoices'}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">Collected</Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {formatCurrency(aging.totalCollected, 'USD')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(aging.collectedPct * 100)}% of invoiced
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Aging bar chart (only when there is open AR) */}
              {aging.totalOutstanding > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Aging Receivables (days past due)
                  </Typography>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={aging.buckets} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={(v: number) => formatCurrency(v, 'USD')}
                        width={80}
                      />
                      <RechartsTooltip
                        formatter={(value: number, _name: string, props: any) => [
                          `${formatCurrency(value, 'USD')} (${props.payload.count} ${props.payload.count === 1 ? 'invoice' : 'invoices'})`,
                          'Outstanding',
                        ]}
                      />
                      <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                        {aging.buckets.map((b) => (
                          <Cell key={b.key} fill={AGING_COLORS[b.key] || '#90a4ae'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              )}
            </Box>
          ) : null}

          {/* Filters */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyOpen}
                  onChange={handleShowOnlyOpenChange}
                  color="primary"
                />
              }
              label="Only Show Open Invoices"
            />
            {selectedVendorId && (
              <Chip
                label={`Filtered by: ${summary.find((s) => s.vendorId === selectedVendorId)?.subsidiaryName || 'Selected Subsidiary'}`}
                onDelete={() => updateSearchParams({ vendorId: null, page: '0' })}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {/* Invoices Table */}
          <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : invoices.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No invoices found.
          </Alert>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={40}></TableCell>
                  <TableCell
                    onClick={() => handleSort('doc_number')}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Doc Number
                      {renderSortIcon('doc_number')}
                    </Box>
                  </TableCell>
                  <TableCell>Subsidiary</TableCell>
                  <TableCell
                    onClick={() => handleSort('customer_name')}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Customer
                      {renderSortIcon('customer_name')}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="right"
                    onClick={() => handleSort('total_amt')}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      Total Amount
                      {renderSortIcon('total_amt')}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="right"
                    onClick={() => handleSort('balance')}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      Balance
                      {renderSortIcon('balance')}
                    </Box>
                  </TableCell>
                  <TableCell>Corresponding Bill ID</TableCell>
                  <TableCell>Private Note</TableCell>
                  <TableCell
                    onClick={() => handleSort('metadata_create_time')}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Created
                      {renderSortIcon('metadata_create_time')}
                    </Box>
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sync Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow
                    key={invoice.id}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                    onClick={() => navigate(`/admin/hub/accounting/invoices/${invoice.id}`)}
                  >
                    <TableCell sx={{ px: 1 }}>
                      <NoteIcon hasNotes={invoice.hasNotes || false} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {invoice.invoiceNumber || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {invoice.subsidiaryName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {invoice.customerName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrency(invoice.totalAmount, invoice.currencyCode)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={invoice.balanceDue > 0 ? 'error' : 'text.primary'}>
                        {formatCurrency(invoice.balanceDue, invoice.currencyCode)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {invoice.correspondingBillId ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/hub/accounting/bills/${invoice.correspondingBillId}`);
                          }}
                        >
                          {invoice.correspondingBillId}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {invoice.privateNote ? (
                        <Tooltip title={invoice.privateNote}>
                          <Typography variant="body2" sx={{ cursor: 'help' }}>
                            {truncateText(invoice.privateNote)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(invoice.createdTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusChipProps(invoice.status).label}
                        color={getStatusChipProps(invoice.status).color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {invoice.syncError ? (
                        <Tooltip title={invoice.syncError}>
                          <Chip
                            icon={<ErrorIcon />}
                            label="Error"
                            color="error"
                            size="small"
                            sx={{ cursor: 'help' }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          label="OK"
                          color="success"
                          size="small"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};